import React from 'react';
import type { Category } from '../services/catalog-service';
import { 
  fetchCategories, 
  fetchCatalog, 
  fetchProductsByCategory,
  fetchProductDetail,
  fetchAromas,
  fetchWaxes,
  fetchColors,
  fetchHolders
} from '../services/catalog-service';

/**
 * Hook for fetching categories data
 * 
 * @returns {Object} The categories data, loading state, and error state
 */
export const useCategories = () => {
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const loadCategories = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchCategories();
      // If backend provides an ordering field (id / order / position), sort by it
      const sortKeyCandidates = ['id', 'order', 'position', 'sort_order'];
      if (Array.isArray(data)) {
        const hasNumericKey = sortKeyCandidates.find(key => data.every(item => item && typeof (item as any)[key] === 'number'));
        if (hasNumericKey) {
          const sorted = [...data].sort((a: any, b: any) => (a[hasNumericKey] as number) - (b[hasNumericKey] as number));
          setCategories(sorted);
        } else {
          setCategories(data);
        }
      } else {
        // Defensive: if fetchCategories unexpectedly returns non-array, set empty
        setCategories([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch categories'));
      // On error, leave categories empty (no local mock fallback)
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  return { categories, isLoading, error, refetch: loadCategories };
};

/**
 * Hook for fetching products by category
 * 
 * @param {string} categoryId - The category identifier
 * @returns {Object} The products data, loading state, and error state
 */
export const useProductsByCategory = (categoryId, filters: Record<string, any> = {}) => {
  // Pagination-aware products hook
  const PAGE_SIZE = 20;
  const [products, setProducts] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [next, setNext] = React.useState<string | null>(null);
  const [previous, setPrevious] = React.useState<string | null>(null);
  const [count, setCount] = React.useState<number | null>(null);
  const currentPageRef = React.useRef<number>(1);

  const reset = React.useCallback(() => {
    setProducts([]);
    setNext(null);
    setPrevious(null);
    setCount(null);
    currentPageRef.current = 1;
    setError(null);
  }, []);

  const loadPage = React.useCallback(async (page = 1, opts: { append?: boolean; url?: string; filters?: Record<string, any> } = {}) => {
    if (!categoryId) {
      reset();
      return;
    }

    // choose loading flag based on append
    if (opts.append) setIsLoadingMore(true);
    else setIsLoading(true);
    setError(null);

    try {
      const effectiveFilters = opts.filters || filters || {};
      const data = await fetchProductsByCategory(
        categoryId,
        opts.url
          ? { url: opts.url }
          : { page, page_size: PAGE_SIZE, filters: effectiveFilters }
      );
      const results = data && data.results ? data.results : [];

      if (opts.append) {
        // dedupe by id
        setProducts((prev) => {
          const ids = new Set(prev.map(p => p.id));
          const appended = results.filter((p: any) => !ids.has(p.id));
          return prev.concat(appended);
        });
      } else {
        setProducts(results);
      }

      setNext(data.next || null);
      setPrevious(data.previous || null);
      setCount(typeof data.count === 'number' ? data.count : (results ? results.length : 0));
      currentPageRef.current = page;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(`Failed to fetch products for category: ${categoryId}`));
      // On error, clear products for non-append loads
      if (!opts.append) {
        setProducts([]);
        setCount(0);
      }
    } finally {
      if (opts.append) setIsLoadingMore(false);
      else setIsLoading(false);
    }
  }, [categoryId, reset]);

  const loadNext = React.useCallback(async () => {
    if (!next) return;
    // If next is an absolute/relative URL, use it. Фильтры уже зашиты в ссылке next на стороне бэка.
    await loadPage(currentPageRef.current + 1, { append: true, url: next });
  }, [next, loadPage]);

  // initial load / reset when category changes
  // Reset and reload when categoryId or filters change
  React.useEffect(() => {
    reset();
    // load first page with current filters
    loadPage(1, { append: false, filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, JSON.stringify(filters || {})]);

  return {
    products,
    isLoading,
    isLoadingMore,
    error,
    next,
    previous,
    count,
    loadNext,
    // refetch всегда использует актуальные filters из замыкания
    refetch: () => loadPage(1, { append: false, filters }),
    reset,
  };
};

/**
 * Hook for fetching catalog data (all products)
 * 
 * @returns {Object} The catalog data structure, loading state, and error state
 */
export const useCatalog = () => {
  const [catalog, setCatalog] = React.useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const loadCatalog = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchCatalog();
      setCatalog(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch catalog'));
      // On error, set empty catalog
      setCatalog({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  return { catalog, isLoading, error, refetch: loadCatalog };
};

/**
 * Hook for fetching product details
 * 
 * @param {number|string} productId - The product identifier
 * @returns {Object} The product details, loading state, and error state
 */
export const useProductDetail = (productId, initialProduct: any = null) => {
  const [product, setProduct] = React.useState<any>(initialProduct || null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const loadProduct = React.useCallback(async () => {
    if (!productId) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      // If initialProduct exists and matches requested id, skip fetching
      if (initialProduct && Number(initialProduct.id) === Number(productId)) {
        setProduct(initialProduct);
      } else {
        const data = await fetchProductDetail(productId);
        setProduct(data || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(`Failed to fetch product details for: ${productId}`));
      // On error leave product as null / initialProduct
    } finally {
      setIsLoading(false);
    }
  }, [productId, initialProduct]);

  React.useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  return { product, isLoading, error, refetch: loadProduct };
};

/**
 * Hook for fetching aromas list
 * Returns array of objects { id?, name }
 */
export const useAromas = (search?: string) => {
  // Pagination-aware aromas hook (load-more) with optional server-side search (debounced)
  const PAGE_SIZE = 20;
  const [aromas, setAromas] = React.useState<Array<{ id?: number; name: string }>>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [next, setNext] = React.useState<string | null>(null);
  const [count, setCount] = React.useState<number | null>(null);
  const currentPageRef = React.useRef<number>(1);
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>(search || '');
  const searchRef = React.useRef<string>(debouncedSearch);

  // debounce incoming search prop (300ms)
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search || '');
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const reset = React.useCallback(() => {
    setAromas([]);
    setNext(null);
    setCount(null);
    currentPageRef.current = 1;
    setError(null);
  }, []);

  const loadPage = React.useCallback(async (page = 1, opts: { append?: boolean; url?: string; search?: string } = {}) => {
    if (opts.append) setIsLoadingMore(true);
    else setIsLoading(true);
    setError(null);

    try {
      const data = opts.url ? await fetchAromas({ url: opts.url }) : await fetchAromas({ page, page_size: PAGE_SIZE, search: (opts.search != null ? opts.search : debouncedSearch) });
      const results = data && Array.isArray(data.results) ? data.results : [];

      if (opts.append) {
        setAromas(prev => {
          const seen = new Set(prev.map(a => a.id != null ? String(a.id) : String(a.name)));
          const appended = results.filter(r => {
            const key = r.id != null ? String(r.id) : String(r.name);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return prev.concat(appended);
        });
      } else {
        setAromas(results);
      }

      setNext(data.next || null);
      setCount(typeof data.count === 'number' ? data.count : (results ? results.length : 0));
      currentPageRef.current = page;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch aromas'));
      if (!opts.append) {
        setAromas([]);
        setCount(0);
      }
    } finally {
      if (opts.append) setIsLoadingMore(false);
      else setIsLoading(false);
    }
  }, []);

  const loadNext = React.useCallback(async () => {
    if (!next) return;
    // Pass current debouncedSearch so server-side search is preserved when using page-based fetches
    await loadPage(currentPageRef.current + 1, { append: true, url: next, search: debouncedSearch });
  }, [next, loadPage]);

  // Reload when debouncedSearch changes: reset and fetch first page
  React.useEffect(() => {
    searchRef.current = debouncedSearch;
    reset();
    loadPage(1, { append: false, search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return { aromas, isLoading, isLoadingMore, error, next, count, loadNext, refetch: () => loadPage(1, { append: false }), reset };
};

/**
 * Generic hook for dictionary-based filters (waxes, colors, holders)
 */
const createDictionaryHook = (fetchFn: (options?: { page?: number; page_size?: number; url?: string }) => Promise<{ results: Array<{ id?: number; name: string }>; count: number; next: string | null; previous: string | null }>) => {
  return () => {
    const PAGE_SIZE = 100;
    const [items, setItems] = React.useState<Array<{ id?: number; name: string }>>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<Error | null>(null);

    const load = React.useCallback(async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchFn({ page: 1, page_size: PAGE_SIZE });
        setItems(Array.isArray(data.results) ? data.results : []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch dictionary'));
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    React.useEffect(() => {
      load();
    }, [load]);

    return { items, isLoading, error, refetch: load };
  };
};

export const useWaxes = createDictionaryHook(fetchWaxes);
export const useColors = createDictionaryHook(fetchColors);
// useHolders отдельный, чтобы можно было пробрасывать type (flacon/holder) снаружи
export const useHoldersRaw = fetchHolders;
